import { createFileRoute } from "@tanstack/react-router";
import { siteConfig } from "@/config";

export const Route = createFileRoute("/privacy-policy")({
  head: () => ({
    meta: [
      { title: `Privacy Policy | ${siteConfig.siteName}` },
      {
        name: "description",
        content: `Read the ${siteConfig.siteName} privacy policy — what data we collect, how we use it, and your rights.`,
      },
      { property: "og:title", content: `Privacy Policy — ${siteConfig.siteName}` },
      { property: "og:url", content: `${siteConfig.domain}/privacy-policy` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.domain}/privacy-policy` }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-14">
      <h1 className="text-4xl font-bold tracking-tight text-foreground">Privacy Policy</h1>
      <p className="mt-2 text-sm text-muted-foreground">Last updated: January 1, 2026</p>

      <div className="prose-article mt-6">
        <p>
          {siteConfig.siteName} ("we", "our", or "us") respects your privacy. This Privacy Policy
          explains what information we collect when you visit {siteConfig.domain}, how we use it,
          who we share it with, and the choices you have. By using this website, you agree to the
          practices described below.
        </p>

        <h2>1. Information we collect</h2>
        <p>
          We collect two categories of information: information you provide directly (for example
          when you fill out our contact form) and information collected automatically when you
          browse the site (for example your IP address, browser type, pages viewed, and
          approximate location).
        </p>

        <h2>2. Cookies and similar technologies</h2>
        <p>
          We use cookies and similar technologies to operate the site, remember your preferences,
          measure traffic, and serve advertising. You can control cookies through your browser
          settings. Disabling cookies may affect site functionality.
        </p>

        <h2>3. Advertising and Google AdSense</h2>
        <p>
          We use Google AdSense to display advertisements. Google, as a third-party vendor, uses
          cookies (including the DoubleClick DART cookie) to serve ads based on your visits to
          this site and other sites on the internet. You may opt out of personalized advertising
          by visiting Google's{" "}
          <a
            href="https://www.google.com/settings/ads"
            className="text-primary hover:underline"
            rel="noopener noreferrer"
            target="_blank"
          >
            Ads Settings
          </a>
          . Third-party vendors and ad networks may also use cookies on this site; you can review
          their practices and opt out via the{" "}
          <a
            href="https://optout.aboutads.info/"
            className="text-primary hover:underline"
            rel="noopener noreferrer"
            target="_blank"
          >
            Digital Advertising Alliance opt-out page
          </a>
          .
        </p>

        <h2>4. Analytics</h2>
        <p>
          We use analytics providers (which may include Google Analytics) to understand how
          readers use the site. These providers collect anonymized information about your
          activity on the site to help us improve content and performance.
        </p>

        <h2>5. How we use information</h2>
        <p>
          We use the information we collect to operate and improve the site, respond to your
          inquiries, personalize your experience, serve relevant advertising, detect and prevent
          fraud, and comply with legal obligations.
        </p>

        <h2>6. Sharing of information</h2>
        <p>
          We do not sell your personal information. We share limited information with service
          providers (such as hosting, analytics, and advertising partners) that help us operate
          the site, and we may disclose information if required by law.
        </p>

        <h2>7. Your rights</h2>
        <p>
          Depending on where you live, you may have rights under applicable laws such as the
          California Consumer Privacy Act (CCPA) and the EU General Data Protection Regulation
          (GDPR), including the right to access, correct, or delete your personal information.
          To exercise these rights, please contact us using the details on our contact page.
        </p>

        <h2>8. Children's privacy</h2>
        <p>
          This site is not directed to children under 13, and we do not knowingly collect
          personal information from children under 13.
        </p>

        <h2>9. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the
          "Last updated" date at the top of this page. Material changes will be communicated on
          the site.
        </p>

        <h2>10. Contact us</h2>
        <p>
          If you have questions about this Privacy Policy or our data practices, please visit our
          contact page and send us a message.
        </p>
      </div>
    </section>
  );
}
